"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState, type FormEvent, type KeyboardEvent } from "react";
import { Notification } from "@/components/ui/notification";
import { HeroCtaButton } from "@/components/ui/hero-cta-button";
import { isLightPage } from "@/lib/light-pages";

const HERO_GRADIENT_TEXT =
  "bg-gradient-to-r from-emerald-600 via-teal-600 to-green-600 bg-clip-text text-transparent";

export function NewsletterSection() {
  const pathname = usePathname();
  const lightPage = isLightPage(pathname);
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);

  const validateEmail = (value: string): string | null => {
    if (!value.trim()) {
      return "Veuillez saisir une adresse email.";
    }

    if (!value.includes("@")) {
      return "L'adresse mail saisie est incomplète.";
    }

    const parts = value.split("@");
    if (parts.length !== 2) {
      return "L'adresse email ne peut contenir qu'un seul symbole \"@\".";
    }

    const [localPart, domain] = parts;

    if (!localPart || localPart.trim().length === 0) {
      return "La partie avant \"@\" ne peut pas être vide.";
    }

    if (!domain || domain.trim().length === 0) {
      return "La partie après \"@\" ne peut pas être vide.";
    }

    if (!domain.includes(".")) {
      return "L'adresse email doit contenir un point (.) après le symbole \"@\".";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return "Le format de l'adresse email est invalide.";
    }

    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const error = validateEmail(email);
    if (error) {
      setNotification({ message: error, type: "error" });
      return;
    }

    setIsLoading(true);
    setNotification(null);

    try {
      const response = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        setNotification({
          message: data.error || "Une erreur est survenue. Veuillez réessayer.",
          type: "error",
        });
        return;
      }

      setNotification({
        message: data.message || "Inscription réussie !",
        type: "success",
      });
      setEmail("");
    } catch {
      setNotification({
        message: "Erreur de connexion. Vérifiez votre connexion internet et réessayez.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!isLoading && email.trim()) {
        void handleSubmit(e as unknown as FormEvent);
      }
    }
  };

  return (
    <section
      className={`px-6 py-10 sm:py-12 ${
        lightPage ? "bg-[#f3f3f3]" : "border-t border-white/10 bg-(--color-background-strong)"
      }`}
    >
      <div className="mx-auto flex max-w-2xl flex-col items-center gap-5 text-center sm:gap-6">
        <div className="space-y-2">
          <h2
            className={`mx-auto max-w-4xl text-balance text-[1.5rem] font-bold leading-[1.08] tracking-[-0.04em] sm:text-3xl md:text-4xl md:leading-[1.05] ${
              lightPage ? "text-[#1C1C1C]" : "text-white"
            }`}
          >
            5 points à <span className={HERO_GRADIENT_TEXT}>absolument</span> avoir sur votre site
          </h2>
          <p
            className={`mx-auto max-w-md text-pretty text-sm font-medium sm:text-base ${
              lightPage ? "text-zinc-500" : "text-white/70"
            }`}
          >
            La checklist pour vérifier en 5 minutes si votre site a tout ce qu&apos;il
            faut pour convertir vos visiteurs en clients.
          </p>
        </div>

        <div className="w-full space-y-4 rounded-3xl bg-gradient-to-br from-emerald-600 via-teal-600 to-green-600 p-5 shadow-[0_12px_40px_-12px_rgba(16,185,129,0.4)] sm:space-y-5 sm:p-6">
          <form onSubmit={handleSubmit} className="w-full" noValidate>
            <div className="flex w-full items-center gap-1.5 rounded-full border border-white/20 bg-white p-1.5 shadow-sm">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Votre adresse email"
                disabled={isLoading}
                className="min-w-0 flex-1 rounded-full border-0 bg-transparent px-4 py-2.5 text-[15px] text-[#1C1C1C] placeholder:text-zinc-400 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50 sm:py-3 sm:text-base"
              />
              <HeroCtaButton
                as="button"
                type="submit"
                disabled={isLoading}
                className="shrink-0"
              >
                {isLoading ? "..." : "Recevoir gratuitement"}
              </HeroCtaButton>
            </div>
          </form>

          {notification && (
            <Notification
              message={notification.message}
              type={notification.type}
              isVisible={!!notification}
              onClose={() => setNotification(null)}
            />
          )}

          <p className="mx-auto max-w-sm text-xs leading-relaxed text-white/75">
            En vous inscrivant, vous acceptez notre{" "}
            <Link
              href="/politique-de-confidentialite"
              className="text-white underline underline-offset-2 transition hover:text-white/90"
            >
              politique de confidentialité
            </Link>
            .
          </p>
        </div>
      </div>
    </section>
  );
}
